import { Injectable, isDevMode } from '@angular/core';
import { environment } from 'src/environments/environment';

declare const analytics: any;

interface EventTrackGovernance {
  object: string;
  action: string;
  properties?: any;
}

export interface TrackEvent {
  object: string;
  action: string;
  properties?: any;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticService {
  private userUid?: string;

  private accountUid?: string;

  private sessionUid?: string;

  set setUserUid(userUid: string) {
    // If the user UID is changed, an alias event must be triggered to assign the old ID to the new user
    if (this.isAnalyticsValid && this.userUid && this.userUid !== userUid) {
      analytics.alias(userUid);
    }
    this.userUid = userUid;
  }

  setSessionUid() {
    const interval = setInterval(() => {
      if (typeof analytics !== 'undefined' && typeof analytics.user === 'function' && analytics.user()) {
        clearInterval(interval);
        this.sessionUid = analytics.user().anonymousId();
      }
    }, 250);

    setTimeout(() => clearInterval(interval), 5000);
  }

  set setAccountUid(accountUid: string) {
    this.accountUid = accountUid;
  }

  get getUserUid(): string {
    return this.userUid as string;
  }

  get getAccountUid(): string {
    return this.accountUid as string;
  }

  get getSessionUid(): string {
    return this.sessionUid as string;
  }

  get areCookiesEnabled() {
    if (navigator.cookieEnabled) {
      return true;
    }

    // Browsers that dont support navigator.cookieEnabled
    document.cookie = 'enabled-cookies-test=1; SameSite=None; Secure';

    const enabled = document.cookie.indexOf('enabled-cookies-test=') !== -1;

    // Expire test cookie
    document.cookie = 'enabled-cookies-test=1; SameSite=None; Secure; expires=Thu, 01-Jan-1970 00:00:01 GMT';

    return enabled;
  }

  track({ object, action, properties }: EventTrackGovernance, event = null) {
    if (!this.isAnalyticsValid) {
      return;
    }
    if (event) {
      properties = {
        ...properties,
        // @ts-ignore
        'metaData.target': event.target?.className ?? null,
        // @ts-ignore
        'metaData.innerText': event.target?.innerText ?? null,
      };
    }
    analytics.track(`${object} - ${action}`, {
      ...properties,
      ...this.sessionData,
    });
  }

  page() {
    if (!this.isAnalyticsValid) {
      return;
    }
    analytics.page();
  }

  get isAnalyticsValid(): boolean {
    // If analytics are undefined log an error and exit
    if (typeof analytics === 'undefined') {
      console.warn("[WebBuy] 'analytics' is not defined.  Analytics will not be loaded.");
      return false;
    }
    // Check whether the app is in dev mode, or is in the analytics testing stage
    return !isDevMode() || environment.stage.includes('analytics') || environment.stage.includes('stage');
  }

  private get sessionData() {
    return {
      userUid: this.getUserUid,
      accountUid: this.getAccountUid,
      sessionUid: this.getSessionUid,
    };
  }
}
